import {
  DeleteSeason,
  EditProgram,
  EditVideo,
  Media,
  MediaFolder,
  Program,
  Stores,
  Video,
} from "../types";
import { makeAutoObservable } from "mobx";
import api from "../services/api";

export class ProgramStore {
  stores: Stores;

  video: Video[] = [];

  programs: Program[] = [];

  program: Program | undefined = undefined;

  mediaFolders: MediaFolder[] = [];
  mediaList: Media[] = [];
  mediaListTotal: number = 0;

  isLoading = false;
  private _vimeoMediaFoldersError = false;
  private _vimeoMediaListError = false;

  clientsFilters: {
    [dataKey: string]: string | string[];
  } = {};

  constructor(stores: Stores) {
    this.stores = stores;

    makeAutoObservable(this);
  }

  get vimeoMediaFoldersError() {
    return this._vimeoMediaFoldersError;
  }

  set vimeoMediaFoldersError(vimeoMediaFoldersError) {
    this._vimeoMediaFoldersError = vimeoMediaFoldersError;
  }

  get vimeoMediaListError() {
    return this._vimeoMediaListError;
  }

  set vimeoMediaListError(vimeoMediaListError) {
    this._vimeoMediaListError = vimeoMediaListError;
  }

  getProgram = async () => {
    this.isLoading = true;
    try {
      let program = await api.getProgram();

      this.setPrograms(program);
    } catch (err) {
      throw err;
    } finally {
      this.isLoading = false;
    }
  };

  getProgramByID = async (id: string) => {
    this.isLoading = true;
    try {
      let program = await api.getProgramByID(id);
      program.multimedia = program.multimedia.sort(
        (a: any, b: any) => a.episodeNumber - b.episodeNumber
      );

      this.program = program;
    } catch (err) {
      throw err;
    } finally {
      this.isLoading = false;
    }
  };

  addProgram = async (data: EditProgram) => {
    this.isLoading = true;
    try {
      let program = await api.addProgram(data);

      this.setPrograms([program, ...this.programs]);
    } catch (err) {
      throw err;
    } finally {
      this.isLoading = false;
    }
  };

  updateProgram = async (data: EditProgram) => {
    this.isLoading = true;
    try {
      const programUpdate = await api.updateProgram(data);

      this.setPrograms(
        this.programs.map((program: Program) => {
          if (program.id === programUpdate.id) {
            return programUpdate;
          }
          return program;
        })
      );

      this.getProgramByID(String(data.id));
    } catch (err) {
      throw err;
    } finally {
      this.isLoading = false;
    }
  };

  deleteProgram = async (id: string) => {
    this.isLoading = true;
    try {
      await api.deleteProgram(id);
      this.setPrograms(
        this.programs.filter(
          (program) => program.id !== (id as unknown as number)
        )
      );
    } catch (err) {
      throw err;
    } finally {
      this.isLoading = false;
    }
  };

  deleteSeason = async (id: number, data: DeleteSeason) => {
    this.isLoading = true;
    try {
      await api.deleteSeason(id, data);
    } catch (err) {
      throw err;
    } finally {
      this.isLoading = false;
    }
  };

  getVideo = async (seasonNumber: number, programID: number) => {
    this.isLoading = true;
    try {
      let video = await api.getVideo(seasonNumber, programID);
      this.video = video;
    } catch (err) {
      throw err;
    } finally {
      this.isLoading = false;
    }
  };

  addVideo = async (data: EditVideo) => {
    this.isLoading = true;

    try {
      let video = await api.addVideo(data);

      if (
        this.program &&
        this.program.seasons &&
        video &&
        video.season &&
        this.program.seasons[video.season]
      ) {
        this.program.seasons[video.season].episodes.push(video);
      }
    } catch (err) {
      throw err;
    } finally {
      this.isLoading = false;
    }
  };

  updateVideo = async (data: EditVideo) => {
    this.isLoading = true;
    try {
      await api.updateVideo(data);
    } catch (err) {
      throw err;
    } finally {
      this.isLoading = false;
    }
  };

  updateVideosEpisodeNumbers = async (
    video: Video,
    oldIndex: number,
    newIndex: number
  ) => {
    this.isLoading = true;
    try {
      // We add +1 because the virtualized table returns the index of the row and not the
      // episode number.
      const updated = await api.updateVideosEpisodeNumbers(
        video,
        oldIndex + 1,
        newIndex + 1
      );

      if (!updated) {
        throw new Error("Could not update episode order number");
      }
    } catch (err) {
      throw err;
    } finally {
      this.isLoading = false;
    }
  };

  deleteVideo = async (id: string) => {
    this.isLoading = true;
    try {
      await api.deleteVideo(id);
    } catch (err) {
      throw err;
    } finally {
      this.isLoading = false;
    }
  };

  fetchMediaList = async (
    folderId?: MediaFolder["projectId"],
    page?: number,
    limit?: number,
    query?: string
  ) => {
    this.isLoading = true;
    try {
      const mediaListData = await api.getUploadedVideos(
        folderId,
        page,
        limit,
        query
      );

      if (!mediaListData) {
        throw new Error("Vimeo error");
      }

      this.mediaList = mediaListData.videos;
      this.mediaListTotal = mediaListData.videosTotal || 0;
    } catch (err) {
      this.vimeoMediaListError = true;
    } finally {
      this.isLoading = false;
    }
  };

  fetchMediaFolders = async (page?: number, limit?: number, query?: string) => {
    this.isLoading = true;
    try {
      const mediaFolders = await api.getVideoFolders(page, limit, query);

      if (!mediaFolders) {
        throw new Error("Vimeo error");
      }

      this.mediaFolders = mediaFolders;
    } catch (err) {
      this.vimeoMediaFoldersError = true;
    } finally {
      this.isLoading = false;
    }
  };

  setPrograms = (programs: Program[]) => {
    this.programs = programs;
  };

  setMediaFolders = (mediaFolders: MediaFolder[]) => {
    this.mediaFolders = mediaFolders;
  };

  setMediaList = (mediaList: Media[]) => {
    this.mediaList = mediaList;
  };

  setMediaListTotal = (mediaListTotal: number) => {
    this.mediaListTotal = mediaListTotal;
  };
}
